/**
 * 时长档合身度信号(2026-09-13 D 批抽出)—— 「你写的这些装进这个时长档合不合身」的**只提示**框。
 *
 * ★D 批把它从 `VideoShootPlanCard` ④ 搬进目的流右栏「这条片」摘要面板(设计稿 7d586023 V4):
 *   合身度是对「文案量 × 时长档」的判断,和右栏那句「约 N 镜」同属「这条片会长成什么样」,
 *   理应同处一栏。JSX + testid(shoot-plan-fit / shoot-plan-fit-retier)原样搬来,
 *   `VideoShootPlanCard`(showFit 时)与摘要面板共用这一份,不再各写一遍那段琥珀框。
 * ★判据(sparse/dense/fit)仍是 `durationFit` 单一源;本组件只渲染 fit 结果,不自算。
 */
import { AlertTriangle } from "lucide-react";

import type { Dispatch, SetStateAction } from "react";
import type { DurationFit } from "../../model/durationFit";
import type { FormState } from "../../model/videoCreateTypes";

import { DURATION_OPTIONS } from "./VideoFormatRow";

interface Props {
  /** durationFit(form, beatPlan) 的结果;null 或 state==="fit" → 不渲染(不打扰)。 */
  fit: DurationFit | null;
  setForm: Dispatch<SetStateAction<FormState>>;
  disabled?: boolean;
}

export function DurationFitSignal({ fit, setForm, disabled }: Props) {
  if (!fit || fit.state === "fit") return null;
  const suggestOption = fit.suggestTier
    ? DURATION_OPTIONS.find((o) => o.value === fit.suggestTier)
    : null;
  const contentBeats = fit.contentBeats;

  return (
    <div
      data-testid="shoot-plan-fit"
      data-fit-state={fit.state}
      className="flex flex-wrap items-start gap-x-2 gap-y-1.5 rounded-md border border-amber-400/40 bg-amber-500/5 px-3 py-2 text-2xs leading-relaxed text-amber-700 dark:border-amber-400/30 dark:text-amber-400"
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} />
      <span className="min-w-0 flex-1">
        {fit.state === "sparse" ? (
          <>
            你写的内容偏少,而选了 {fit.targetSec}s 长档 —— 成片容易拖节奏、反复凑时长。
            {contentBeats ? `这个档要讲约 ${contentBeats} 个点,` : ""}
            建议改短一档,或往「想拍什么」里补几个卖点/场景。
          </>
        ) : (
          <>
            你写的内容偏多,而选了 {fit.targetSec}s 短档 —— 口播念不完会被裁、画面也会赶。
            建议改长一档,让每个点都讲透。
          </>
        )}
      </span>
      {suggestOption && (
        <button
          type="button"
          disabled={disabled}
          data-testid="shoot-plan-fit-retier"
          onClick={() => setForm((f) => ({ ...f, briefDuration: suggestOption.value }))}
          className="shrink-0 cursor-pointer rounded border border-amber-500/50 px-2 py-0.5 font-medium text-amber-800 transition-colors hover:bg-amber-100/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:pointer-events-none disabled:opacity-50 dark:text-amber-300 dark:hover:bg-amber-950/40"
        >
          改成 {suggestOption.label}
        </button>
      )}
    </div>
  );
}
